import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface ServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  longDescription: string;
  imageUrl: string;
}

const ServiceModal: React.FC<ServiceModalProps> = ({ isOpen, onClose, title, longDescription, imageUrl }) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center p-4 z-[60] transition-opacity duration-300 ease-in-out overflow-y-auto">
      <div 
        ref={modalContentRef} 
        className="bg-gray-900 shadow-xl rounded-lg max-w-2xl w-full relative transform transition-all duration-300 ease-in-out scale-100 my-8 border border-gray-700"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors z-10"
          aria-label="Close service details"
        >
          <X size={24} />
        </button>
        
        {imageUrl && (
          <div className="w-full h-64 overflow-hidden rounded-t-lg">
            <img src={imageUrl} alt={`${title} service illustration`} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="p-6 md:p-8">
          <h2 className="text-2xl font-bold text-white sm:text-3xl mb-4">
            {title}
          </h2>
          <div 
            className="prose prose-invert prose-sm sm:prose-base max-w-none text-gray-300"
            dangerouslySetInnerHTML={{ __html: longDescription }}
          />
          <div className="mt-8 text-right">
            <button
              onClick={onClose} // Or could link to contact modal: onOpenContactModal()
              className="px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal; 